import { useEffect, useMemo, useState } from "react";
import type { OperationsJourneyCarrierKey, OperationsJourneyItemDto, OperationsJourneyItemsDto, OperationsLensKey, OperationsPenKey, OperationsStageKey, OperationsStartRiskKey } from "@/api/endpoints";
import { BrandedLoader } from "@/components/BrandedLoader";
import { TableSkeleton } from "@/components/Skeleton";
import { fmtETDate } from "@/lib/format";
import { actorRoleLabel, ATTENTION_CLASS, ATTENTION_LABEL, INTAKE_SOURCE_LABEL, JOURNEY_CARRIER_LABEL, PEN_LABEL, STAGE_LABEL, START_RISK_CLASS, START_RISK_LABEL, workTypeLabel } from "../operationsLabels";
import { FacetGroup, FacetSelect, SearchBox } from "./DrawerFilterBar";
import { StartDateCell } from "./StartRiskCells";

interface Props {
  title: string;
  /** Set when the drawer was opened from a start-date tile (2026-10-26): the list reads as a run-sheet sorted by the lead. */
  cohort?: "atRisk" | "breached";
  data: OperationsJourneyItemsDto | undefined;
  loading: boolean;
  /** The rows shown belong to the previous filters while the new ones load (placeholder data): dim them and say so. */
  updating?: boolean;
  error: string | null;
  /** Opened from the owner table's team / division row (2026-10-26): the local filter starts on it and that dropdown is hidden. */
  presetTeam?: string;
  presetDivision?: string;
  /** Opened from the owner table's manager row (2026-10-27). */
  presetManager?: string;
  onClose: () => void;
  onRetry: () => void;
}

interface JourneyView {
  attention: string[];
  carrier: string[];
  workType: string[];
  startRisk: string[];
  /** The Where chips on a start-date cohort: the carrying request's stage. */
  stage: string[];
  /** The Owner, Team and Division dropdowns (2026-10-26); Manager (2026-10-27). */
  owner: string[];
  team: string[];
  division: string[];
  manager: string[];
  /** The Engagement type dropdown (2026-10-28). */
  engagement: string[];
  /** The "Who holds the pen" chips (2026-10-28): the carrying record's holder. */
  holder: string[];
  q: string;
}

/** The Where chips in lifecycle order. */
const STAGE_ORDER_FOR_WHERE: OperationsStageKey[] = ["requestProcessing", "releaseToSubmit", "submitToApprove", "awaitingClose"];

const EMPTY_VIEW: JourneyView = { attention: [], carrier: [], workType: [], startRisk: [], stage: [], owner: [], team: [], division: [], manager: [], engagement: [], holder: [], q: "" };

/** Counts of one field over the items, highest first, a sentinel (Unassigned, No team) last. */
function countBy(items: OperationsJourneyItemDto[], pick: (j: OperationsJourneyItemDto) => string | null, last: string): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const j of items) {
    const v = pick(j);
    if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => Number(a.value === last) - Number(b.value === last) || b.count - a.count || a.value.localeCompare(b.value));
}

/** "Work order · to submit" / the actor's name with the role after it / the intake's owner, on one line. */
function PendingWithCell({ j }: { j: OperationsJourneyItemDto }) {
  if (j.carrier === "none") return <span className="muted">—</span>;
  if (j.pendingKind === "workOrder") {
    return <span className="ops-pill role-workorder" title="No task is pending; the work order is waiting on our side.">{j.pendingWith ? `Work order · ${j.pendingWith}` : "Work order"}</span>;
  }
  if (j.pendingKind === "task") {
    const role = j.pendingRole ?? "Other";
    return (
      <span className="ops-pending">
        <span className={`ops-pill role-${role.toLowerCase()}`} title="Who holds the first open task">{j.pendingWith ?? actorRoleLabel(role)}</span>
        {j.pendingWith && <span className="muted">{actorRoleLabel(role)}</span>}
      </span>
    );
  }
  return j.pendingWith ? <span>{j.pendingWith}</span> : <span className="muted">Unassigned</span>;
}

export function journeyHref(link: { kind: "intake" | "request"; source: string | null; id: string }): string {
  return link.kind === "intake"
    ? `/intake/${link.source}/${encodeURIComponent(link.id)}?via=operations`
    : `/requests/${encodeURIComponent(link.id)}?via=operations`;
}

/** The journey's records, oldest first, each a link: the intake, the pre-verification, the onboarding request. */
function records(j: OperationsJourneyItemDto): { label: string; href: string; title: string }[] {
  const out: { label: string; href: string; title: string }[] = [];
  if (j.intake) {
    const system = j.intake.sourceSystem?.replace(/^Procom/i, "") ?? null;
    const ref = j.intake.sourceReference ? `${system ? `${system} ` : ""}${j.intake.sourceReference}` : INTAKE_SOURCE_LABEL[j.intake.source];
    out.push({ label: `Intake ${ref}`, href: journeyHref({ kind: "intake", source: j.intake.source, id: j.intake.id }), title: `Open the intake (${j.intake.status ?? "status unknown"}) in a new tab` });
  }
  if (j.preOnboardingRequestId) out.push({ label: "Pre-Verification", href: journeyHref({ kind: "request", source: null, id: j.preOnboardingRequestId }), title: "Open the pre-verification request in a new tab" });
  if (j.onboardingRequestId) out.push({ label: j.workType, href: journeyHref({ kind: "request", source: null, id: j.onboardingRequestId }), title: `Open the ${j.workType.toLowerCase()} request in a new tab` });
  return out;
}

function matches(item: OperationsJourneyItemDto, view: JourneyView): boolean {
  if (view.attention.length > 0 && !view.attention.includes(item.attention)) return false;
  if (view.carrier.length > 0 && !view.carrier.includes(item.carrier)) return false;
  if (view.workType.length > 0 && !view.workType.includes(item.workType)) return false;
  if (view.startRisk.length > 0 && !view.startRisk.includes(item.startRisk)) return false;
  if (view.stage.length > 0 && !view.stage.includes(item.stage ?? "")) return false;
  if (view.owner.length > 0 && !view.owner.includes(item.owner ?? "")) return false;
  if (view.team.length > 0 && !view.team.includes(item.team ?? "")) return false;
  if (view.division.length > 0 && !view.division.includes(item.division ?? "")) return false;
  if (view.manager.length > 0 && !view.manager.includes(item.manager ?? "")) return false;
  if (view.engagement.length > 0 && !view.engagement.includes(item.engagementType)) return false;
  if (view.holder.length > 0 && !view.holder.includes(item.holder ?? "")) return false;
  if (view.q) {
    const q = view.q.trim().toLowerCase();
    const hay = [item.worker, item.client, item.pendingWith, item.intake?.sourceReference, item.onboardingRequestId, item.preOnboardingRequestId, item.workOrderId]
      .filter((v) => v !== null && v !== undefined)
      .join(" ")
      .toLowerCase();
    if (q && !hay.includes(q)) return false;
  }
  return true;
}

/**
 * Drill-through for the Flow card's Onboarding / Redeployment rows and for every bar of the age card:
 * one row per consultant journey (intake + pre-verification + request); on an age bar the other open
 * requests (extensions, documents, changes) appear as one-record rows with a Work type chip to tell
 * them apart. The API returns the whole cohort (its cap is well above the weekly numbers), so the
 * chips and the search filter locally and nothing is stored in the URL. A row opens the record
 * carrying the journey; the Records cell links the others.
 */
export function JourneyDrawer({ title, cohort, data, loading, updating, error, presetTeam, presetDivision, presetManager, onClose, onRetry }: Props) {
  const baseView = useMemo<JourneyView>(() => ({ ...EMPTY_VIEW, team: presetTeam ? [presetTeam] : [], division: presetDivision ? [presetDivision] : [], manager: presetManager ? [presetManager] : [] }), [presetTeam, presetDivision, presetManager]);
  const [view, setView] = useState<JourneyView>(baseView);
  const items = useMemo(() => data?.items ?? [], [data]);
  const shown = useMemo(() => items.filter((i) => matches(i, view)), [items, view]);
  // Filtered = the user changed something beyond the preset (a team / division drill starts on one).
  const filtered = JSON.stringify(view) !== JSON.stringify(baseView) && (view.attention.length > 0 || view.carrier.length > 0 || view.workType.length > 0 || view.startRisk.length > 0 || view.stage.length > 0 || view.owner.length > 0 || view.team.length > 0 || view.division.length > 0 || view.manager.length > 0 || view.engagement.length > 0 || view.holder.length > 0 || view.q !== "");
  const ownerFacets = useMemo(() => countBy(items, (j) => j.owner, "Unassigned"), [items]);
  const teamFacets = useMemo(() => countBy(items, (j) => j.team, "No team"), [items]);
  const divisionFacets = useMemo(() => countBy(items, (j) => j.division, "No division"), [items]);
  const managerFacets = useMemo(() => countBy(items, (j) => j.manager, "No manager"), [items]);
  const engagementFacets = useMemo(() => countBy(items, (j) => j.engagementType, "Not set"), [items]);
  // Only open journeys hold the pen: a started / ended cohort has no holder and the group hides itself.
  const holderFacets = useMemo(() => countBy(items, (j) => j.holder, "external"), [items]);
  const startRiskCohort = cohort !== undefined;
  // The Where chips: the carrying request's stage, counted over the cohort (the API has no facet for it).
  const stageFacets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of items) if (i.stage) counts.set(i.stage, (counts.get(i.stage) ?? 0) + 1);
    return STAGE_ORDER_FOR_WHERE.filter((s) => counts.has(s)).map((s) => ({ value: s, count: counts.get(s)! }));
  }, [items]);
  const ageBin = data?.ageBin ?? null;
  const flow = ageBin !== null ? "open" : (data?.flow ?? "open");
  const workTypeFacets = data?.workTypeFacets ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const primaryHref = (j: OperationsJourneyItemDto): string | null => {
    if (j.carrierLink) return journeyHref(j.carrierLink);
    const last = records(j).at(-1);
    return last?.href ?? null;
  };
  const open = (j: OperationsJourneyItemDto) => {
    const href = primaryHref(j);
    if (href) window.open(href, "_blank", "noopener");
  };
  const cols = startRiskCohort ? 8 : 9;

  return (
    <>
      <div className="ops-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="ops-drawer" role="dialog" aria-modal="true" aria-labelledby="ops-drawer-title">
        <div className="ops-drawer-head">
          <div>
            <h2 id="ops-drawer-title">{title}</h2>
            <div className="meta">
              {data ? `${shown.length}${shown.length < items.length ? ` of ${items.length}` : ""} journey${shown.length === 1 ? "" : "s"} · ${cohort === "atRisk" ? "soonest start first" : cohort === "breached" ? "most overdue first" : "most severe first, then longest waiting"}` : "Loading…"}
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </div>
        <div className="ops-drawer-note">
          {startRiskCohort ? (
            <>
              {cohort === "atRisk"
                ? "Each row is an open onboarding journey or extension whose work-order version starts within the window and is not approved. "
                : "Each row is an open onboarding journey or extension whose work-order version started already (or today) and is still not approved. "}
              &ldquo;Where&rdquo; says which step is left, &ldquo;Pending with&rdquo; who holds it now. A row opens the carrying record in a new tab; the Records cell links the others.
              {data?.note && <> <b>{data.note}</b></>}
            </>
          ) : (
            <>
          {ageBin !== null
            ? `Each row is an open consultant journey, or another open request (extension, document, change), aged ${ageBin > 30 ? "more than 30" : ageBin} business days; “Carried by” says which record holds a journey. `
            : flow === "started"
              ? "Each row is a consultant journey whose earliest record (intake, pre-verification or request) was created in the period, whatever became of it since. "
              : flow === "ended"
                ? "Each row is a journey that ended in the period: its work order approved (Onboarding, Redeployment) or its request completed (the other types). "
                : "Each row is a consultant journey with an open record right now; “Carried by” says which record holds it. "}
          The intake, the pre-verification and the onboarding request of one consultant count once; on the other rows an intake request and the FBO request of the same type on its work order count once. &ldquo;Waiting&rdquo; counts business days since the
          journey started. Attention is the carrying record&rsquo;s. A row opens the carrying record in a new tab; the Records cell links the others.
          {data?.note && <> <b>{data.note}</b></>}
            </>
          )}
        </div>
        <div className="ops-drawer-filters">
          <FacetGroup
            label="Attention"
            values={data?.attentionFacets ?? []}
            selected={view.attention}
            labelOf={(v) => ATTENTION_LABEL[v as OperationsLensKey] ?? v}
            dotClass={(v) => `att-${ATTENTION_CLASS[v as OperationsLensKey] ?? "unknown"}`}
            onChange={(attention) => setView((s) => ({ ...s, attention }))}
          />
          {!startRiskCohort && (
            <FacetGroup
              label="Start date"
              values={data?.startRiskFacets ?? []}
              selected={view.startRisk}
              labelOf={(v) => START_RISK_LABEL[v as OperationsStartRiskKey] ?? v}
              dotClass={(v) => `risk-${START_RISK_CLASS[v as OperationsStartRiskKey] ?? "none"}`}
              onChange={(startRisk) => setView((s) => ({ ...s, startRisk }))}
            />
          )}
          <FacetGroup
            label="Who holds the pen"
            values={holderFacets}
            selected={view.holder}
            labelOf={(v) => PEN_LABEL[v as OperationsPenKey] ?? v}
            dotClass={(v) => `holder-${v}`}
            onChange={(holder) => setView((s) => ({ ...s, holder }))}
          />
        </div>
        <div className="ops-drawer-filters ops-drawer-filters-row2">
          {startRiskCohort ? (
            <FacetSelect id="ops-jd-where" label="Where" values={stageFacets} selected={view.stage} labelOf={(v) => STAGE_LABEL[v as OperationsStageKey] ?? v} onChange={(stage) => setView((s) => ({ ...s, stage }))} />
          ) : (
            <FacetSelect id="ops-jd-carrier" label="Carried by" values={data?.carrierFacets ?? []} selected={view.carrier} labelOf={(v) => JOURNEY_CARRIER_LABEL[v as OperationsJourneyCarrierKey] ?? v} onChange={(carrier) => setView((s) => ({ ...s, carrier }))} />
          )}
          <FacetSelect id="ops-jd-worktype" label="Work type" values={workTypeFacets} selected={view.workType} labelOf={workTypeLabel} onChange={(workType) => setView((s) => ({ ...s, workType }))} />
          <FacetSelect id="ops-jd-engagement" label="Engagement type" values={engagementFacets} selected={view.engagement} labelOf={(v) => v} onChange={(engagement) => setView((s) => ({ ...s, engagement }))} />
          {!presetDivision && !presetTeam && !presetManager && <FacetSelect id="ops-jd-division" label="Division" values={divisionFacets} selected={view.division} labelOf={(v) => v} onChange={(division) => setView((s) => ({ ...s, division }))} />}
          {!presetTeam && !presetManager && <FacetSelect id="ops-jd-manager" label="Manager" values={managerFacets} selected={view.manager} labelOf={(v) => v} onChange={(manager) => setView((s) => ({ ...s, manager }))} />}
          {!presetTeam && <FacetSelect id="ops-jd-team" label="Team" values={teamFacets} selected={view.team} labelOf={(v) => v} onChange={(team) => setView((s) => ({ ...s, team }))} />}
          <FacetSelect id="ops-jd-owner" label="Owner" values={ownerFacets} selected={view.owner} labelOf={(v) => v} onChange={(owner) => setView((s) => ({ ...s, owner }))} />
          <SearchBox value={view.q} onChange={(q) => setView((s) => ({ ...s, q }))} placeholder="Worker, client, request ID, WO or reference" />
        </div>
        {filtered && !error && (
          <div className="ops-drawer-status" role="status">
            Showing <b>{shown.length}</b>{shown.length < items.length ? ` of ${items.length}` : ""}
            <button type="button" className="btn btn-text btn-sm" onClick={() => setView(baseView)}>Clear filters</button>
          </div>
        )}
        {updating && (
          <div className="ops-drawer-status ops-drawer-updating" role="status">
            <BrandedLoader variant="inline" label="Updating…" />
          </div>
        )}
        <div className={`ops-drawer-body${updating ? " is-stale" : ""}`} aria-busy={updating || undefined}>
          {loading && !data ? (
            <TableSkeleton cols={cols} rows={6} />
          ) : error ? (
            <div className="empty">
              <h4>Could not load the journeys</h4>
              <p>{error}</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>Retry</button>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl ops-items ops-journey-items">
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Client</th>
                    {startRiskCohort ? (
                      <>
                        <th className="fit" title="The work-order version's start date, with the lead in business days.">Starts</th>
                        <th className="fit" title="The step left before approval: the carrying request's stage, with its open task actions.">Where</th>
                      </>
                    ) : (
                      <th>Carried by</th>
                    )}
                    <th>Pending with</th>
                    <th>Attention</th>
                    <th className="numeric">Waiting (bd)</th>
                    {!startRiskCohort && <th className="fit" title="The carrying record's work-order version start date with the lead in business days; the pill marks it At risk or Breached.">Start</th>}
                    {!startRiskCohort && <th className="fit">Started</th>}
                    <th>Records</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={cols}>
                        {filtered ? (
                          <div className="empty">
                            <h4>No journeys match these filters</h4>
                            <p>Remove a filter, or <button type="button" className="btn btn-text btn-sm" onClick={() => setView(baseView)}>clear all filters</button>.</p>
                          </div>
                        ) : (
                          <div className="empty"><h4>Nothing here</h4><p>No journeys in this bucket.</p></div>
                        )}
                      </td>
                    </tr>
                  )}
                  {shown.map((j) => {
                    const href = primaryHref(j);
                    return (
                      <tr key={j.key} className={href ? "linkrow" : undefined} onClick={() => open(j)}>
                        <td className="worker">
                          {href ? (
                            <a className="row-link" href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`Open the record carrying ${j.worker ?? "this journey"} in a new tab`}>
                              <b>{j.worker ?? "—"}</b>
                              <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
                            </a>
                          ) : (
                            <b>{j.worker ?? "—"}</b>
                          )}
                          {j.workOrderId && <small>WO {j.workOrderId}</small>}
                        </td>
                        <td className="client">{j.client ?? <span className="muted">—</span>}</td>
                        {startRiskCohort ? (
                          <>
                            <StartDateCell risk={j.startRisk} leadBd={j.startLeadBd} startDate={j.startDate} hidePill />
                            <td className="fit">
                              {j.stage ? (
                                <>
                                  <span className="ops-pill stage">{STAGE_LABEL[j.stage]}</span>
                                  {j.openTaskCount != null && j.openTaskCount > 0 && <span className="sub muted">{j.openTaskCount} {j.openTaskCount === 1 ? "task" : "tasks"} open</span>}
                                </>
                              ) : (
                                <span className="ops-pill stage">{JOURNEY_CARRIER_LABEL[j.carrier]}</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <td className="fit">
                            <span className="ops-pill stage" title={j.endedUtc ? `Work order approved on ${fmtETDate(j.endedUtc)}` : undefined}>
                              {j.endedUtc ? "WO approved" : JOURNEY_CARRIER_LABEL[j.carrier]}
                            </span>
                          </td>
                        )}
                        <td className="pending"><PendingWithCell j={j} /></td>
                        <td className="reason">
                          {j.carrier === "none" ? (
                            <span className="muted">—</span>
                          ) : (
                            <>
                              <span className={`ops-pill att-${ATTENTION_CLASS[j.attention as OperationsLensKey] ?? "unknown"}`}><i className="dot" />{ATTENTION_LABEL[j.attention as OperationsLensKey] ?? j.attention}</span>
                              {j.reason && <span className="r muted" title={j.reason}>{j.reason}</span>}
                            </>
                          )}
                        </td>
                        <td className="numeric">{j.waitingBd}</td>
                        {!startRiskCohort && <StartDateCell risk={j.startRisk} leadBd={j.startLeadBd} startDate={j.startDate} />}
                        {!startRiskCohort && <td className="fit nowrap">{fmtETDate(j.startedUtc)}</td>}
                        <td className="nowrap">
                          {records(j).map((r, idx) => (
                            <span key={r.href}>
                              {idx > 0 && " · "}
                              <a href={r.href} target="_blank" rel="noopener noreferrer" title={r.title} onClick={(e) => e.stopPropagation()}>{r.label}</a>
                            </span>
                          ))}
                          {records(j).length === 0 && <span className="muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="ops-drawer-foot">
          {data && data.totalCount > data.cap ? `Showing the first ${data.cap} of ${data.totalCount}; narrow with the chips or the search.` : startRiskCohort ? "Sorted by start date: the soonest, or the most overdue, first." : "Sorted by attention (Blocked, Stuck, Clear), then by waiting."}
        </div>
      </aside>
    </>
  );
}
